import { useEffect, useState } from 'react';
import { Storage } from 'aws-amplify';
import { css } from '@emotion/react';

import { Button, LinearProgress } from '@lego/klik-react';
import { Download } from '@lego/klik-icons-react';

import { Dropzone } from '../../Dropzone';
import { Heading } from '../../Heading';
import { invokeGenerateDataLambda } from '../utils/invokeGenerateDataLambda';
import { logger } from '../../../utils/logger';
import { media } from '../../../config';
import { imageUploadErrorTypes } from '../../../constants';
import { OverlayPortal } from '../../OverlayPortal';
import { Spinner } from '../../spinner/Spinner';
import { UploadedFile } from '../components/UploadedFile';
import { Tooltip } from '../../tooltip/Tooltip';

import {
  addSnackbar,
  snackbarTypes,
  snackbarDefaultMessages,
} from '../../../utils/snackbar';
import { spinnerOverlayText } from '../../../styles/common';

const bucketName = process.env.CUSTOMER_MAPPING_BUCKET;
const s3StorageOptions = {
  bucket: bucketName,
  level: 'private',
};
const acceptedFiles = ['.xlsx'];

const progressBarContainer = css({
  [media.medium]: {
    width: 400,
  },
});

const uploadDropzoneWrapperStyle = css({
  width: '50%',
});

const exportIconStyle = css({
  height: 14,
});

const downloadLinkStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
});

const downloadLinkWrapperStyle = css({
  display: 'flex',
});

const uploadRejected = (errorsArray) => {
  const error = errorsArray[0].errors[0];
  let errorMessage;

  switch (error.code) {
    case imageUploadErrorTypes.FILE_INVALID_TYPE:
      errorMessage = 'Invalid file type. Only Excel worksheets are allowed.';
      break;

    case imageUploadErrorTypes.TOO_MANY_FILES:
      errorMessage = 'Too many files. Only one file at a time is allowed.';
      break;

    case imageUploadErrorTypes.FILE_TOO_LARGE:
      errorMessage = 'File is too large. Max size is 10 MB.';
      break;

    default:
      errorMessage = snackbarDefaultMessages.errorText;
  }

  return addSnackbar({
    header: snackbarDefaultMessages.errorHeader,
    text: errorMessage,
    type: snackbarTypes.ERROR,
  });
};

const ChannelSurveyMapping = () => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [latestFileUrl, setLatestFileUrl] = useState(null);
  const [latestFileLoading, setLatestFileLoading] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);

  const requestDataGenerate = async () => {
    setGenerateLoading(true);
    invokeGenerateDataLambda(currentFile.path, 'channel-survey')
      .then(() => {
        setCurrentFile(null);
      })
      .catch((error) => {
        logger.logError(error);
      })
      .finally(() => {
        setGenerateLoading(false);
        fetchLatestFileUrl();
      });
  };

  const fetchLatestFileUrl = () => {
    setLatestFileLoading(true);
    Storage.get('channel-survey/latest.xlsx', {
      bucket: bucketName,
      level: 'public',
      customPrefix: { public: '' },
    })
      .then((url) => setLatestFileUrl(url))
      .finally(() => setLatestFileLoading(false));
  };

  useEffect(() => {
    fetchLatestFileUrl();
  }, []);

  const uploadChannelSurveyFile = async (file) => {
    if (!file) return false;
    const filePath = 'channel-survey/upload/' + file.path;
    try {
      await Storage.put(filePath, file, {
        bucket: bucketName,
        level: 'private',
        contentType: file.type,
        progressCallback(progress) {
          const uploadProgressInPercent = Math.round(
            (progress.loaded / progress.total) * 100
          );
          setUploadProgress(uploadProgressInPercent);
        },
      });
      setCurrentFile(file);
      setUploadProgress(0);
    } catch (error) {
      logger.logError(error);
    }
  };

  const deleteFile = async () => {
    const filePath = 'channel-survey/upload/' + currentFile.path;
    try {
      await Storage.remove(filePath, s3StorageOptions);
      setCurrentFile(null);
    } catch (error) {
      logger.logError(error);
    }
  };

  const generateData = async () => {
    setGenerateLoading(true);
    await requestDataGenerate();
  };

  if (generateLoading) {
    return (
      <OverlayPortal>
        <Spinner />
        <p css={spinnerOverlayText}>
          Processing channel survey mapping. This may take a few minutes.
        </p>
      </OverlayPortal>
    );
  }

  return (
    <div>
      {latestFileLoading ? (
        <div css={progressBarContainer}>
          <LinearProgress />
        </div>
      ) : (
        latestFileUrl && (
          <div css={downloadLinkWrapperStyle}>
            <Tooltip content="Download latest channel survey mapping file">
              <a
                href={latestFileUrl}
                target="_blank"
                rel="noreferrer"
                css={downloadLinkStyle}
              >
                <Download css={exportIconStyle} />
                Export channel survey mapping
              </a>
            </Tooltip>
          </div>
        )
      )}
      <div css={uploadDropzoneWrapperStyle}>
        <Heading type="h3" text="Upload new channel/survey mapping" />
        <Dropzone
          acceptedFiles={acceptedFiles}
          uploadProgress={uploadProgress}
          onDrop={(fileDropped) => uploadChannelSurveyFile(fileDropped[0])}
          onDropRejected={uploadRejected}
          isDisabled={!!uploadProgress}
        />
      </div>
      {currentFile && (
        <UploadedFile uploadedFile={currentFile} onDelete={deleteFile} />
      )}

      <div style={{ marginTop: !currentFile ? '48px' : '0px' }}>
        <Button
          label="Process Upload"
          data-transaction-name="Process Upload - Channel Survey Mapping"
          disabled={!currentFile}
          onClick={() => generateData()}
        />
      </div>
    </div>
  );
};

export { ChannelSurveyMapping };
